import { useThrottledCallback } from '@mantine/hooks'
import { useCallback } from 'react'

/**
 * The function returned by useThrottledCallback won't return the return value from origin function.
 * This hook was created to return the return value from origin function.
 */
export const useThrottledCallbackWithReturnValue = <
  T extends (...args: any[]) => any,
>(
  ...params: Parameters<typeof useThrottledCallback<T>>
) => {
  const [callback, wait] = params
  const passReturnValueWithFunction = useCallback(
    (ret: (returnValue: ReturnType<T>) => void, ...args: Parameters<T>) => {
      ret(callback(...args))
    },
    [callback],
  )
  const throttledWithoutReturnValue = useThrottledCallback(
    passReturnValueWithFunction,
    wait,
  )
  const throttledWithReturnValue = useCallback(
    (...args: Parameters<T>) => {
      let returnValue: ReturnType<T> | undefined
      throttledWithoutReturnValue(
        v => {
          returnValue = v
        },
        ...args,
      )
      return returnValue
    },
    [throttledWithoutReturnValue],
  )
  return throttledWithReturnValue
}
